import { lazy, Suspense, useEffect, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { SellOrderDetail } from "../schema/sell-order.schema";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import {
  fetchSellOrderHandler,
  sendSellOrderCommunicationHandler,
  handleExecuteSellOrderHandler,
  handleRejectSellOrderHandler,
  sendSellOrderNegotiationHandler,
  updateSellOrderAmountHandler,
} from "../schema/handler-functions";
import { SellOrderAmountAndActions } from "@/components/common/section-components/requests/sell-order/SellOrderAmountAndActions";

import { SellOrderMainDataCard } from "@/components/common/section-components/card/SellOrderMainDataCard";
import { NegotiationLastMessage } from "@/components/common/section-components/chat/NegotiationLastMessage";
import { RequestTypes } from "../../../../constants/enums";
import { useRoles } from "@/hooks/useRoles";

export const SellOrderDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.SELL_ORDERS);
  };

  const [sellOrder, setSellOrder] = useState<SellOrderDetail | null>();
  const fetchSellOrder = async (showLoad: boolean = true) => {
    await fetchSellOrderHandler({
      fetchPermissions,
      id,
      navigate,
      setLoadingSellOrder,
      setSellOrder,
      showLoad,
    });
  };

  const [loadingSellOrder, setLoadingSellOrder] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchSellOrder();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectSellOrder, setLoadingRejectSellOrder] =
    useState<boolean>(false);
  const [loadingExecuteSellOrder, setLoadingExecuteSellOrder] =
    useState<boolean>(false);
  const [loadingSendNegotiation, setLoadingSendNegotiation] =
    useState<boolean>(false);
  const [loadingSendCommunication, setLoadingSendCommunication] =
    useState<boolean>(false);

  const handleExecuteSellOrder = async (event?: FieldValues) => {
    await handleExecuteSellOrderHandler({
      navigate,
      setLoadingExecuteSellOrder,
      sellOrder,
      fetchPermissions,
      watchedValues: event,
    });
  };

  const { open: openExecuteModal, setOpen: setOpenExecuteModal } =
    useDisclosure();

  const { open: openCommunication, setOpen: setOpenCommunication } =
    useDisclosure();

  const { open: openChatViewModal, setOpen: setOpenChatViewModal } =
    useDisclosure();

  const handleRejectSellOrder = async (event?: FieldValues) => {
    await handleRejectSellOrderHandler({
      navigate,
      setLoadingRejectSellOrder,
      setOpenRejectModal,
      event,
      fetchPermissions,
      sellOrder,
    });
  };

  const handleSendNegotiation = async () => {
    await sendSellOrderNegotiationHandler({
      navigate,
      setLoadingSendNegotiation,
      sellOrder,
      fetchSellOrder,
      fetchPermissions,
    });
  };

  const handleSendCommunication = async (event?: FieldValues) => {
    // No se pasa setOpenCommunicationModal: el chat es multi-turno y debe
    // permanecer abierto después de enviar.
    return await sendSellOrderCommunicationHandler({
      navigate,
      setLoadingSendCommunication,
      event,
      sellOrder,
      fetchSellOrder,
      fetchPermissions,
    });
  };

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const { fetchPermissions } = useRoles();

  const handleEditAmount = async () => {
    updateSellOrderAmountHandler({
      errors,
      setLoadingEditAmount,
      sellOrder,
      fetchSellOrder,
      setIsEditingAmount,
      watchedValues,
      fetchPermissions,
      navigate,
    });
  };

  useEffect(() => {
    if (sellOrder && !sellOrder?.sinBase) {
      setValue("sell_order_amount", sellOrder?.montoSolicitado);
    }
  }, [sellOrder]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Orden de venta"}
        onBack={handleBack}
        backButton
        loading={loadingSellOrder}
      />

      {!loadingSellOrder && (
        <>
          <SellOrderAmountAndActions
            clearErrors={clearErrors}
            sellOrder={sellOrder}
            setIsEditingAmount={setIsEditingAmount}
            control={control}
            errors={errors}
            handleEditAmount={handleEditAmount}
            loadingEditAmount={loadingEditAmount}
            isEditingAmount={isEditingAmount}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectSellOrder={loadingRejectSellOrder}
            loadingExecuteSellOrder={loadingExecuteSellOrder}
            loadingSendNegotiation={loadingSendNegotiation}
            loadingSendCommunication={loadingSendCommunication}
            handleExecuteSellOrder={() => setOpenExecuteModal(true)}
            handleSendNegotiation={handleSendNegotiation}
            setOpenCommunicationModal={setOpenCommunication}
            setValue={setValue}
          />

          <SellOrderMainDataCard sellOrder={sellOrder} />

          <NegotiationLastMessage
            title="Último mensaje - Comunicación"
            messages={sellOrder?.mensajes || []}
            onOpenChat={() => setOpenChatViewModal(true)}
            testId="sell-order-open-chat"
          />
        </>
      )}

      {sellOrder?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectSellOrder}
            loadingAccept={loadingRejectSellOrder}
            idTipoSolicitud={RequestTypes.OrdenDeVenta}
            entityName="orden de venta"
          />
          <ExecuteGrainRequestModalDynamic 
            open={openExecuteModal}
            setOpen={setOpenExecuteModal}
            handleAccept={handleExecuteSellOrder}
            loadingAccept={loadingExecuteSellOrder}
          />
          <NegotiationChatModalDynamic
            open={openCommunication}
            setOpen={setOpenCommunication}
            title="Chat - En comunicación"
            messages={sellOrder?.mensajes || []}
            handleSend={handleSendCommunication}
            loadingSend={loadingSendCommunication}
            idTipoSolicitud={RequestTypes.OrdenDeVenta}
            mensajesDisponibles={sellOrder?.mensajesDisponibles}
          />

          <NegotiationChatModalDynamic
            open={openChatViewModal}
            setOpen={setOpenChatViewModal}
            title="Chat - En comunicación"
            messages={sellOrder?.mensajes || []}
            readOnly
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const ExecuteGrainRequestModalDynamic = lazy(() =>
  import("@/components/modal/ExecuteGrainRequestModal").then((el) => ({
    default: el["ExecuteGrainRequestModal"],
  })),
);

const NegotiationChatModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationChatModal").then((el) => ({
    default: el["NegotiationChatModal"],
  })),
);
